import { type ReactNode, useEffect, useState } from "react";
import { ArrowRight, BarChart3, Database, SearchCheck, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const cloudUrl = "https://cloud.aeokit.dev";

type AnswerEvidence = {
  provider: string;
  model: string;
  observedAt: string;
  mentioned: boolean;
  excerpt: string;
  answer: string;
};

type PromptResult = {
  text: string;
  successfulAnswers: number;
  mentionedAnswers: number;
  mentionRate: number;
  evidence: AnswerEvidence[];
};

type BrandResult = {
  label: string;
  successfulAnswers: number;
  mentionedAnswers: number;
  mentionRate: number;
  prompts: PromptResult[];
};

type BenchmarkResponse = {
  totals: { successfulAnswers: number; mentionedAnswers: number };
  brands: BrandResult[];
  prompts: string[];
  surfaces: Array<{ provider: string; model: string }>;
  updatedAt: string | null;
};

export const benchmarkIndustries = [
  {
    slug: "crm",
    apiSlug: "crm-visibility",
    name: "CRM",
    singular: "CRM brand",
    heading: "CRM AI visibility benchmark",
    description:
      "How often five CRM brands appear in answers to common software buying questions.",
    companies: ["HubSpot", "Salesforce", "Pipedrive", "Zoho CRM", "monday CRM"],
    prompts: [
      "What are the best CRM platforms for a small business?",
      "Which CRM is best for a growing B2B sales team?",
      "Is HubSpot or Salesforce better for a midsize company?",
    ],
  },
  {
    slug: "website-builders",
    apiSlug: "website-builders",
    name: "Website builders",
    singular: "website builder",
    heading: "Website builder AI visibility benchmark",
    description:
      "How often five website builders appear in answers to common small-business and SEO questions.",
    companies: ["Wix", "Squarespace", "Webflow", "WordPress.com", "Shopify"],
    prompts: [
      "What are the best website builders for a small business?",
      "Is Wix or Squarespace better for a small business?",
      "Which website builder is best for SEO?",
    ],
  },
  {
    slug: "ecommerce-platforms",
    apiSlug: "ecommerce-platforms",
    name: "Ecommerce platforms",
    singular: "ecommerce platform",
    heading: "Ecommerce platform AI visibility benchmark",
    description:
      "How often five ecommerce platforms appear in answers to common online-store buying questions.",
    companies: ["Shopify", "BigCommerce", "WooCommerce", "Wix", "Squarespace"],
    prompts: [
      "What are the best ecommerce platforms for a small business?",
      "Is Shopify or WooCommerce better for a small online store?",
      "Which ecommerce platform is easiest to use?",
    ],
  },
] as const;

export function benchmarkCompanySlug(label: string) {
  return label
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export const crmCompanies = benchmarkIndustries[0].companies.map((label) => ({
  slug: benchmarkCompanySlug(label),
  label,
}));

function useBenchmark(apiSlug: string) {
  const [data, setData] = useState<BenchmarkResponse | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${cloudUrl}/api/public/benchmarks/${apiSlug}`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Benchmark API ${response.status}`);
        return (await response.json()) as BenchmarkResponse;
      })
      .then(setData)
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setData(null);
      });
    return () => controller.abort();
  }, [apiSlug]);
  return data;
}

function formatUpdated(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
}

function formatSurface(model: string) {
  return (
    {
      "bing-copilot": "Bing Copilot",
      chatgpt: "ChatGPT",
      gemini: "Gemini",
      "google-ai-mode": "Google AI Mode",
      "google-ai-overview": "Google AI Overview",
      perplexity: "Perplexity",
    }[model] ?? model.replaceAll("-", " ")
  );
}

export function HighlightedAnswer({
  text,
  brand,
}: {
  text: string;
  brand: string;
}) {
  const escaped = brand.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const matcher = new RegExp(`(${escaped})`, "gi");
  return (
    <p className="answer-excerpt">
      {text
        .split(matcher)
        .map((part, index) =>
          part.toLocaleLowerCase() === brand.toLocaleLowerCase() ? (
            <mark key={`${part}-${index}`}>{part}</mark>
          ) : (
            part
          ),
        )}
    </p>
  );
}

function highlightChildren(children: ReactNode, brand: string): ReactNode {
  if (typeof children !== "string") return children;
  const escaped = brand.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const matcher = new RegExp(`(${escaped})`, "gi");
  return children
    .split(matcher)
    .map((part, index) =>
      part.toLocaleLowerCase() === brand.toLocaleLowerCase() ? (
        <mark key={`${part}-${index}`}>{part}</mark>
      ) : (
        part
      ),
    );
}

function FullAnswer({ answer, brand }: { answer: string; brand: string }) {
  const highlighted = ({ children }: { children?: ReactNode }) =>
    highlightChildren(children, brand);
  return (
    <div className="full-answer">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: highlighted,
          li: highlighted,
          td: highlighted,
          th: highlighted,
          strong: highlighted,
        }}
      >
        {answer}
      </ReactMarkdown>
    </div>
  );
}

export function AnswerModal({
  brand,
  prompt,
  evidence,
  onClose,
}: {
  brand: string;
  prompt: string;
  evidence: AnswerEvidence;
  onClose: () => void;
}) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  const surface = formatSurface(evidence.model);
  return (
    <div
      className="answer-modal-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className={`answer-modal surface-${evidence.model}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="answer-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="answer-modal-header">
          <div className="surface-identity">
            <span aria-hidden="true">{surface.slice(0, 1)}</span>
            <div>
              <strong id="answer-modal-title">{surface} answer</strong>
              <small>Captured view · recorded by AeoKit</small>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close answer"
            autoFocus
          >
            <X />
          </button>
        </header>
        <div className="answer-modal-conversation">
          <div className="answer-prompt">
            <span>You asked</span>
            <p>{prompt}</p>
          </div>
          <div className="answer-response" data-surface={surface}>
            <div className="answer-response-meta">
              <strong>{surface}</strong>
              <span
                className={evidence.mentioned ? "mentioned" : "not-mentioned"}
              >
                {evidence.mentioned
                  ? `${brand} mentioned`
                  : `${brand} not mentioned`}
              </span>
            </div>
            <div className="surface-answer-author">
              <span aria-hidden="true">{surface.slice(0, 1)}</span>
              <strong>{surface}</strong>
            </div>
            <FullAnswer answer={evidence.answer} brand={brand} />
          </div>
        </div>
        <footer className="answer-modal-footer">
          <span>Observed {formatUpdated(evidence.observedAt)}</span>
          <span>
            Full captured answer · presentation reconstructed by AeoKit via{" "}
            {evidence.provider}
          </span>
        </footer>
      </section>
    </div>
  );
}

function Methodology() {
  return (
    <section
      className="benchmark-methodology"
      aria-labelledby="methodology-title"
    >
      <div>
        <p className="eyebrow">How to read the benchmark</p>
        <h2 id="methodology-title">Measured answers, visible denominator.</h2>
      </div>
      <div className="benchmark-method-grid">
        <article>
          <SearchCheck />
          <h3>Mention rate</h3>
          <p>
            Successful answers mentioning the monitored brand divided by all
            successful answers for that brand.
          </p>
        </article>
        <article>
          <Database />
          <h3>Failed runs excluded</h3>
          <p>
            Provider failures are not treated as brand misses. This can produce
            different denominators between brands.
          </p>
        </article>
        <article>
          <BarChart3 />
          <h3>Not recommendation share</h3>
          <p>
            A mention does not imply positive sentiment, recommendation rank,
            factual accuracy, or an owned citation.
          </p>
        </article>
      </div>
    </section>
  );
}

export function BenchmarksPage() {
  return (
    <>
      <section className="benchmark-hero">
        <div className="container narrow">
          <p className="eyebrow">Live answer visibility data</p>
          <h1>Industry AI visibility benchmarks</h1>
          <p>
            Compare which companies appear in real AI answers, inspect the exact
            prompts, and open the complete captured evidence.
          </p>
        </div>
      </section>
      <section className="section benchmark-directory-section">
        <div className="container">
          <div className="benchmark-page-heading">
            <div>
              <p className="eyebrow">Available benchmarks</p>
              <h2>Explore by industry</h2>
            </div>
            <span>Live data from AeoKit Cloud</span>
          </div>
          <div className="benchmark-company-grid">
            {benchmarkIndustries.map((industry) => (
              <a href={`/benchmarks/${industry.slug}/`} key={industry.slug}>
                <div>
                  <strong>{industry.name}</strong>
                  <span>{industry.companies.length}</span>
                </div>
                <p>{industry.description}</p>
                <ArrowRight aria-hidden="true" />
              </a>
            ))}
          </div>
          <Methodology />
        </div>
      </section>
    </>
  );
}

export function IndustryBenchmarkPage({
  industrySlug,
}: {
  industrySlug: string;
}) {
  const industry = benchmarkIndustries.find(
    (item) => item.slug === industrySlug,
  );
  const data = useBenchmark(industry?.apiSlug ?? industrySlug);
  if (!industry) return null;
  const companies = industry.companies.map((label) => ({
    label,
    slug: benchmarkCompanySlug(label),
  }));
  return (
    <>
      <section className="benchmark-hero">
        <div className="container narrow">
          <p className="eyebrow">Live industry benchmark</p>
          <h1>{industry.heading}</h1>
          <p>{industry.description} Measured across live AI answer surfaces.</p>
        </div>
      </section>
      <section className="section benchmark-directory-section">
        <div className="container">
          <div className="benchmark-page-heading">
            <div>
              <p className="eyebrow">Current comparison</p>
              <h2>Brand mention visibility</h2>
            </div>
            <span>
              {data?.updatedAt
                ? `Updated ${formatUpdated(data.updatedAt)}`
                : "Loading live results…"}
            </span>
          </div>
          <div className="benchmark-company-grid">
            {companies.map((company) => {
              const result = data?.brands.find(
                (brand) => brand.label === company.label,
              );
              return (
                <a
                  href={`/benchmarks/${industry.slug}/${company.slug}/`}
                  key={company.slug}
                >
                  <div>
                    <strong>{company.label}</strong>
                    <span>{result ? `${result.mentionRate}%` : "Live"}</span>
                  </div>
                  <p>
                    {result
                      ? `${result.mentionedAnswers} of ${result.successfulAnswers} successful answers`
                      : "Loading production measurements"}
                  </p>
                  <ArrowRight aria-hidden="true" />
                </a>
              );
            })}
          </div>
          <div className="benchmark-questions">
            <h2>What we asked</h2>
            <ol>
              {industry.prompts.map((prompt) => (
                <li key={prompt}>{prompt}</li>
              ))}
            </ol>
            <p>
              Surfaces:{" "}
              {data
                ? data.surfaces.map(({ model }) => model).join(", ")
                : "ChatGPT, Gemini, Bing Copilot, Google AI Mode, and Google AI Overviews"}
              .
            </p>
          </div>
          <Methodology />
        </div>
      </section>
    </>
  );
}

export function CompanyBenchmarkPage({
  industrySlug,
  companySlug,
}: {
  industrySlug: string;
  companySlug: string;
}) {
  const industry = benchmarkIndustries.find(
    (item) => item.slug === industrySlug,
  );
  const companies = (industry?.companies ?? []).map((label) => ({
    label,
    slug: benchmarkCompanySlug(label),
  }));
  const company = companies.find((candidate) => candidate.slug === companySlug);
  const data = useBenchmark(industry?.apiSlug ?? industrySlug);
  const [selectedAnswer, setSelectedAnswer] = useState<{
    prompt: string;
    evidence: AnswerEvidence;
  } | null>(null);
  if (!industry || !company) return null;
  const result = data?.brands.find((brand) => brand.label === company.label);
  return (
    <>
      <section className="benchmark-hero company-benchmark-hero">
        <div className="container narrow">
          <nav className="benchmark-breadcrumb" aria-label="Breadcrumb">
            <a href={`/benchmarks/${industry.slug}/`}>
              {industry.name} benchmark
            </a>
            <span>→</span>
            <span>{company.label}</span>
          </nav>
          <p className="eyebrow">Live company profile</p>
          <h1>{company.label} AI visibility</h1>
          <p>
            How often {company.label} appears in AeoKit’s tracked answers to{" "}
            {industry.name.toLocaleLowerCase()} comparison and buying questions.
          </p>
          <div className="company-score">
            <strong>{result ? `${result.mentionRate}%` : "Loading"}</strong>
            <span>
              {result
                ? `${result.mentionedAnswers} mentions in ${result.successfulAnswers} successful answers`
                : "Fetching the latest production result"}
            </span>
          </div>
        </div>
      </section>
      <section className="section company-results-section">
        <div className="container">
          <div className="benchmark-page-heading">
            <div>
              <p className="eyebrow">Question-level evidence</p>
              <h2>Results by prompt</h2>
            </div>
            <span>
              {data?.updatedAt
                ? `Updated ${formatUpdated(data.updatedAt)}`
                : "Live data loading…"}
            </span>
          </div>
          <div className="prompt-result-list">
            {industry.prompts.map((prompt) => {
              const promptResult = result?.prompts.find(
                (item) => item.text === prompt,
              );
              return (
                <article key={prompt}>
                  <p>{prompt}</p>
                  <strong>
                    {promptResult ? `${promptResult.mentionRate}%` : "—"}
                  </strong>
                  <span>
                    {promptResult
                      ? `${promptResult.mentionedAnswers} of ${promptResult.successfulAnswers} answers`
                      : "Waiting for live data"}
                  </span>
                </article>
              );
            })}
          </div>
          <section
            className="answer-evidence-section"
            aria-labelledby="answer-evidence-title"
          >
            <div className="benchmark-page-heading">
              <div>
                <p className="eyebrow">Inspectable evidence</p>
                <h2 id="answer-evidence-title">Answer evidence</h2>
              </div>
              <span>Representative recent successful answers</span>
            </div>
            {result ? (
              <div className="answer-evidence-groups">
                {result.prompts.map((prompt) => (
                  <section key={prompt.text}>
                    <h3>{prompt.text}</h3>
                    <div>
                      {prompt.evidence.map((evidence) => (
                        <article
                          key={`${evidence.provider}-${evidence.model}-${evidence.observedAt}`}
                        >
                          <header>
                            <strong>{formatSurface(evidence.model)}</strong>
                            <span
                              className={
                                evidence.mentioned
                                  ? "mentioned"
                                  : "not-mentioned"
                              }
                            >
                              {evidence.mentioned
                                ? `${company.label} mentioned`
                                : "Not mentioned"}
                            </span>
                          </header>
                          <HighlightedAnswer
                            text={evidence.excerpt}
                            brand={company.label}
                          />
                          <footer>
                            <span>
                              Observed {formatUpdated(evidence.observedAt)}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedAnswer({
                                  prompt: prompt.text,
                                  evidence,
                                })
                              }
                            >
                              Open answer <ArrowRight />
                            </button>
                          </footer>
                        </article>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="answer-evidence-loading">
                Loading bounded answer excerpts from AeoKit’s public evidence
                API…
              </div>
            )}
            <p className="answer-evidence-note">
              Excerpts are capped for readability. Highlights mark literal
              brand-name matches; a configured alias can still count as a
              detected mention without a visible highlight.
            </p>
          </section>
          <section className="company-interpretation">
            <p className="eyebrow">How to read this profile</p>
            <h2>Presence is only the first question.</h2>
            <p>
              This profile measures whether {company.label} appeared. It does
              not by itself say whether the answer recommended the brand, ranked
              it first, described it positively, stated accurate claims, or
              cited {company.label}’s website.
            </p>
          </section>
          <Methodology />
          <div className="benchmark-related">
            <h2>Compare other {industry.name.toLocaleLowerCase()}</h2>
            <div>
              {companies
                .filter((item) => item.slug !== companySlug)
                .map((item) => (
                  <a
                    href={`/benchmarks/${industry.slug}/${item.slug}/`}
                    key={item.slug}
                  >
                    {item.label}
                    <ArrowRight />
                  </a>
                ))}
            </div>
          </div>
          {selectedAnswer ? (
            <AnswerModal
              brand={company.label}
              prompt={selectedAnswer.prompt}
              evidence={selectedAnswer.evidence}
              onClose={() => setSelectedAnswer(null)}
            />
          ) : null}
        </div>
      </section>
    </>
  );
}

export function CrmBenchmarkPage() {
  return <IndustryBenchmarkPage industrySlug="crm" />;
}

export function CrmCompanyBenchmarkPage({ slug }: { slug: string }) {
  return <CompanyBenchmarkPage industrySlug="crm" companySlug={slug} />;
}
